'use client';

import React, { ReactNode, useEffect, useRef, useState } from 'react';

export type TenantStaffShowcaseMember = {
  id: string;
  firstName?: string;
  displayName: string;
  bio?: string;
  imageUrl?: string;
  socials?: { facebook?: string; instagram?: string; twitter?: string };
  // Rendered by the caller, because the published site builds it on the server.
  bookingAction?: ReactNode;
};

type SocialPlatform = 'instagram' | 'facebook' | 'twitter';

const normalizeExternalUrl = (value?: string): string | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  return /^https?:\/\//i.test(trimmed) ? trimmed : 'https://' + trimmed.replace(/^\/+/, '');
};

// Staff socials are saved as usernames ("@name"), so turn them into profile links.
const normalizeHandleUrl = (platform: 'instagram' | 'twitter', value?: string): string | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const handle = trimmed
    .replace(/^@+/, '')
    .replace(/^www\./i, '')
    .replace(/^(instagram\.com|x\.com|twitter\.com)\//i, '')
    .split(/[/?]/)[0];
  if (!handle) return undefined;
  return platform === 'instagram' ? 'https://instagram.com/' + handle : 'https://x.com/' + handle;
};

const socialLinksFor = (socials: TenantStaffShowcaseMember['socials']): Array<[SocialPlatform, string]> => [
  ['instagram', normalizeHandleUrl('instagram', socials?.instagram)],
  ['facebook', normalizeExternalUrl(socials?.facebook)],
  ['twitter', normalizeHandleUrl('twitter', socials?.twitter)],
].filter((entry): entry is [SocialPlatform, string] => Boolean(entry[1]));

const SOCIAL_LABELS: Record<SocialPlatform, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  twitter: 'X',
};

const SocialIcon = ({ platform }: { platform: SocialPlatform }) => {
  if (platform === 'instagram') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1" className="filled" />
      </svg>
    );
  }
  if (platform === 'facebook') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M14 8h3V4.2c-.6-.1-2-.2-3.4-.2C10.8 4 9 5.7 9 8.8V11H6v4h3v9h4v-9h3.2l.8-4H13V9.2c0-.8.3-1.2 1-1.2Z" className="filled" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 4l11.6 16H20L8.4 4H5Zm2.5 2h1.1l7.9 12h-1.1L7.5 6Z" className="filled" />
    </svg>
  );
};

const hasProfile = (person: TenantStaffShowcaseMember): boolean =>
  Boolean(person.bio?.trim()) || socialLinksFor(person.socials).length > 0;

const CardMedia = ({ person }: { person: TenantStaffShowcaseMember }) => (
  <>
    {person.imageUrl
      ? <img src={person.imageUrl} alt={person.displayName} loading="lazy" />
      : <span className="team-placeholder" aria-hidden="true">{person.displayName.slice(0, 1).toUpperCase()}</span>}
    <span className="team-shade" aria-hidden="true" />
  </>
);

const TenantStaffShowcase: React.FC<{ staff: TenantStaffShowcaseMember[] }> = ({ staff }) => {
  const [selected, setSelected] = useState<TenantStaffShowcaseMember | null>(null);
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!selected) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      // The booking modal opened from this profile handles its own Escape.
      if (event.key === 'Escape' && !document.querySelector('.booking-overlay')) setSelected(null);
    };
    window.addEventListener('keydown', closeOnEscape);
    const opener = openerRef.current;
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
      opener?.focus();
    };
  }, [selected]);

  const selectedSocials = selected ? socialLinksFor(selected.socials) : [];

  return (
    <>
      <div className="team-strip">
        {staff.map((person) => (hasProfile(person) ? (
          <button
            aria-haspopup="dialog"
            className="team-card team-card-button"
            key={person.id}
            onClick={(event) => {
              openerRef.current = event.currentTarget;
              setSelected(person);
            }}
            type="button"
          >
            <CardMedia person={person} />
            <span className="team-content">
              <span className="team-name">{person.displayName}</span>
              <span className="team-profile-cta">View profile <span aria-hidden="true">↗</span></span>
            </span>
          </button>
        ) : (
          <article className="team-card" key={person.id}>
            <CardMedia person={person} />
            <div className="team-content">
              <strong className="team-name">{person.displayName}</strong>
              {person.bookingAction}
            </div>
          </article>
        )))}
      </div>

      {selected ? (
        <div
          className="staff-profile-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) setSelected(null);
          }}
        >
          <section
            aria-labelledby="staff-profile-title"
            aria-modal="true"
            className="staff-profile-dialog"
            role="dialog"
          >
            <div className="staff-profile-photo">
              {selected.imageUrl ? (
                <img src={selected.imageUrl} alt={selected.displayName} />
              ) : (
                <span className="staff-profile-placeholder" aria-hidden="true">
                  {selected.displayName.slice(0, 1).toUpperCase()}
                </span>
              )}
              <div className="staff-profile-shade" aria-hidden="true" />
              <button
                ref={closeButtonRef}
                aria-label="Close profile"
                className="staff-profile-close"
                onClick={() => setSelected(null)}
                type="button"
              >×</button>
            </div>
            <div className="staff-profile-content">
              <h3 id="staff-profile-title">{selected.displayName}</h3>
              {selected.bio?.trim() ? <p>{selected.bio.trim()}</p> : null}
              {selectedSocials.length ? (
                <div className="staff-profile-socials">
                  <span>Follow</span>
                  <div className="social-icons light">
                    {selectedSocials.map(([platform, href]) => (
                      <a key={platform} href={href} target="_blank" rel="noreferrer" aria-label={SOCIAL_LABELS[platform]}>
                        <SocialIcon platform={platform} />
                      </a>
                    ))}
                  </div>
                </div>
              ) : null}
              {selected.bookingAction ? (
                <div className="staff-profile-booking">{selected.bookingAction}</div>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
};

export default TenantStaffShowcase;
